import { useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LogoMark, LogoName } from '@/components/Wordmark'
import { api } from '@/lib/api'
import { authClient } from '@/lib/auth'
import { forgetOfflineCopies } from '@/lib/offline'

// The front door, kept quiet like notesfrom's: the mark above the name, one line, and the form; the rest waits for the first sign-in.
// Two steps: your email, then the 6-digit code sent to it. No passwords.
export function SignIn() {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // A connect link opened in a browser that isn't signed in: on an iPhone, the camera app opens links in Safari, which
  // doesn't share the home-screen app's sign-in. The address stays put, so signing in here carries on to them.
  const connecting = /^\/connect\/[A-Za-z0-9_-]{43}$/.test(location.pathname)

  async function sendCode(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    // Opened from a link invite (made to text or show as a QR code), the email typed here is who the link is for, so
    // the code can be sent to it. Harmless for any other address: an emailed invite or a dead link is left alone.
    const invite = /^\/join\/([A-Za-z0-9_-]{43})$/.exec(location.pathname)?.[1]
    if (invite) await api(`/invites/${invite}/claim`, { method: 'POST', body: { email: email.trim() } }).catch(() => {})
    const { error } = await authClient.emailOtp.sendVerificationOtp({ email: email.trim(), type: 'sign-in' })
    setBusy(false)
    if (error) return setError(error.message ?? 'That didn’t work. Please try again.')
    setSent(true)
  }

  async function signIn(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    const { error } = await authClient.signIn.emailOtp({ email: email.trim(), otp: code.trim() })
    setBusy(false)
    if (error) return setError(error.message ?? 'That code didn’t work. Please try again.')
    await forgetOfflineCopies()
    await queryClient.invalidateQueries({ queryKey: ['me'] })
  }

  return (
    <main className="flex min-h-svh flex-col items-center justify-center px-6 py-16">
      {/* The front door is always light, but a home-screen app added in dark draws the clock white over the page
          (public/theme.js), so it gets a dark strip of its own to stay readable. Elsewhere it's 0 tall. */}
      <div aria-hidden data-slot="clock-strip" className="fixed inset-x-0 top-0 h-(--safe-top) bg-foreground" />
      <div className="flex w-full max-w-sm flex-col items-center text-center">
        <h1 className="flex w-full max-w-72 flex-col items-center gap-6">
          <LogoMark className="h-20" />
          <LogoName className="w-full" />
        </h1>
        <p className="mt-4 text-lg text-balance text-muted-foreground">Your social life and real life, in one place.</p>
        {connecting && (
          <div role="note" className="mt-8 flex w-full flex-col gap-2 rounded-2xl bg-secondary p-4 text-balance">
            <p className="font-semibold">Someone wants to connect with you.</p>
            <p className="text-sub text-muted-foreground">
              Sign in to see who, and you’ll go straight to them. Already have gatheredfrom on your home screen? Open it
              and scan from People’s + instead.
            </p>
          </div>
        )}

        <div className="mt-10 w-full text-left">
          {!sent ? (
            <form onSubmit={sendCode} className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" size="lg" disabled={busy}>
                Send code
              </Button>
            </form>
          ) : (
            <form onSubmit={signIn} className="grid gap-4">
              <p className="text-sm text-muted-foreground">We sent a 6-digit code to {email.trim()}.</p>
              <div className="grid gap-2">
                <Label htmlFor="code">Code</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  className="text-center text-xl tracking-[0.3em]"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
              </div>
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" size="lg" disabled={busy}>
                Sign in
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setSent(false)
                  setCode('')
                  setError('')
                }}
              >
                Use a different email
              </Button>
            </form>
          )}
        </div>

        <p className="mt-10 text-sm text-muted-foreground">Invite-only, and private.</p>
      </div>
    </main>
  )
}
